import { useId, useMemo, useState } from "react";
import { useSnackbar } from "notistack";
import clsx from "clsx";

// Third Party
import { useMutation } from "@tanstack/react-query";

// APIs
import { getSalesLeads } from "@/DataPages/api/crm/queries";
import { createSalesLead } from "@/DataPages/api/crm/mutations";

// Context
import { useCRMContext } from "@/DataPages/context/crm/CRMContext";
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";
import { useLeadsContext } from "@/DataPages/context/crm/LeadsContext";

// Hooks
import { useCommissionCyclesData } from "@/Washouts/hooks/common/useCommissionCyclesData";
import { usePaginatedResource } from "@/Common/hooks/Pagination";
import { buildGetNextPageParam } from "@/Common/hooks/Pagination/helpers";

// MUI
import { Grid, Skeleton, Stack, Typography } from "@mui/material";

// Layouts
import EmptyBody from "@/Layouts/components/Basics/EmptyBody";
import TableLoader from "@/Layouts/components/Skeletons/TableLoader";

// Hooks
import { useAuthorization } from "@/Common/hooks/Authorization/Authorization";

// Components
import SalesLeadsTable from "./SalesLeadsTable";
import AddEditPopupDialog from "./AddEditPopupDialog";
import { SnackbarsActions, SnackbarsContent } from "@/Layouts/components/Snackbars";

// Helpers
import { and } from "@/Common/helpers/sonar";
import {
	allSalesLeadsWereSuccessfullyCreated,
	createBusiness,
	createCommunication,
	createDesiredVehicles,
	createIndividual,
	createPhones,
} from "./helpers";
import { anyErrorInSaleLeadPopup } from "./AddEditPopupDialog/helpers";

// Types
import { IPageParam } from "@/Common/api/types/pagination";
import { TCreateSalesLeadInput } from "./types/salesLeads";
import { Nullable } from "../../../../Common/interfaces/types";

// Images
import emptyTable from "@/assets/images/commons/empty_table.png";

// Constants
import { EMPTY_RESULT, INITIAL_PAGE_PARAM, PAGINATION_SIZE } from "@/DataPages/constants/pagination";
import { CRM_KEYS } from "../constants";
import ACTIONS from "@/Constants/actionsList";

// Context
import { ICreateSalesLeadResponse } from "../../../context/crm/interfaces";

type TSalesLeadsProps = {
	isActiveTab: boolean;
};

function ContentTitle(props: Readonly<{ title: string; isLoading?: boolean }>) {
	const { title } = props;
	return <Typography className={"page-title"}>{title}</Typography>;
}

export default function SalesLeads(props: Readonly<TSalesLeadsProps>) {
	const { isActiveTab } = props;
	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const { locationId = "", selection } = useCRMContext();
	const { cycleId: commissionCycleId = "", payPeriodId = "" } = selection ?? {};
	const { commissionCycle, payPeriod } = useCommissionCyclesData({ commissionCycleId, payPeriodId });

	const { enqueueSnackbar } = useSnackbar();
	const { useAuth } = useAuthorization();

	const hasEditSalesLeadsAccess = useAuth(ACTIONS.EDIT_SALES_LEADS);

	const { data, isLoading, refetch, hasNextPage } = usePaginatedResource({
		queryKey: [CRM_KEYS.QUERY.SALES_LEAD, locationId, commissionCycleId, payPeriodId],
		queryFn: async (params) => {
			const pageParam = params.pageParam as IPageParam<string> | undefined;
			const pagination = pageParam && typeof pageParam === "object" ? pageParam : INITIAL_PAGE_PARAM;
			if (!("after" in pagination) || !("first" in pagination)) {
				throw new Error("PageParam should have properties 'after' and 'first'");
			}
			const paginatedResponse = await getSalesLeads({ locationId, commissionCycleId, payPeriodId, pagination });
			if (!paginatedResponse) {
				return EMPTY_RESULT;
			}
			return paginatedResponse;
		},
		initialPageParam: INITIAL_PAGE_PARAM,
		getNextPageParam: buildGetNextPageParam(PAGINATION_SIZE),
		enabled: and(isActiveTab, !!locationId, !!commissionCycleId, !!payPeriodId),
		fetchAllPages: true,
		staleTime: Infinity,
		gcTime: Infinity,
	});

	const { getSubscription } = useSubscriptions();
	const salesLeadsSubscription = getSubscription([CRM_KEYS.SUBSCRIPTION.SALES_LEAD, locationId, commissionCycleId, payPeriodId]);
	salesLeadsSubscription?.setInvalidate([CRM_KEYS.SUBSCRIPTION.SALES_LEAD, locationId, commissionCycleId, payPeriodId]);
	salesLeadsSubscription?.subscriptions?.onUpdateSalesLeads?.setCallback(() => refetch());

	const { leadInputForm, leadErrors, resetContext, setIsSavingLead } = useLeadsContext();

	const hasErrors = useMemo(() => anyErrorInSaleLeadPopup(leadErrors), [leadErrors]);

	const { mutate: createSalesLeadMutation } = useMutation({
		mutationKey: [CRM_KEYS.MUTATION.CREATE_SALE_LEAD, locationId, commissionCycleId, payPeriodId],
		mutationFn: async ({ input, callback }: { input: TCreateSalesLeadInput; callback?: () => void }) => {
			const response = await createSalesLead({ locationId, commissionCycleId, payPeriodId, input });
			callback?.();
			return response ?? null;
		},
		onSettled(data: Nullable<ICreateSalesLeadResponse>) {
			const { prospect = [] } = data ?? {};

			if (prospect?.length) {
				if (allSalesLeadsWereSuccessfullyCreated(prospect)) {
					showSnackBar("Sales lead created successfully");
				} else {
					const error = prospect.shift()?.error ?? "";
					showSnackBar(error, true);
				}
			}

			onCloseDialog();
			setIsSavingLead(false);
		},
	});

	const dataToShow = Boolean(data?.length);

	if (isLoading) {
		return (
			<Grid item xs={12} mt={1}>
				<Skeleton className="relative top-[0px]" variant="text" width={500} height={30} />
				<TableLoader toolbar buttons={3} content rows={5} multiTitles={5} />
			</Grid>
		);
	}

	const handleOpenDialog = () => {
		if (hasEditSalesLeadsAccess) {
			setIsDialogOpen(true);
		}
	};

	const handleCloseDialog = () => {
		onCloseDialog();
	};

	function onCloseDialog() {
		setIsDialogOpen(false);
		resetContext();
	}

	const handleAddItem = async (callback?: () => void) => {
		setIsSavingLead(true);
		const business = createBusiness(leadInputForm.customer.type, leadInputForm.customer);
		const individual = createIndividual(leadInputForm.customer.type, leadInputForm.customer);
		const communication = createCommunication(leadInputForm.customer);
		const phones = createPhones(leadInputForm.customer.phones);
		const desiredVehicles = createDesiredVehicles(leadInputForm.vehicle.desiredVehicle);

		const input: TCreateSalesLeadInput = {
			customer: {
				business,
				individual,
				communication,
				address: leadInputForm.customer.address,
				contact: {
					phones,
					email: leadInputForm.customer.email,
				},
			},
			prospect: {
				type: leadInputForm.prospect.type,
				providerName: leadInputForm.prospect.providerName,
				providerService: leadInputForm.prospect.providerService,
				status: leadInputForm.prospect.status,
				statusType: leadInputForm.prospect.statusType,
				customerInitiated: leadInputForm.prospect.customerInitiated,
				salesPerson: leadInputForm.prospect.salesPerson,
				secondarySalesPerson: leadInputForm.prospect.secondarySalesPerson,
				bdcUser: leadInputForm.prospect.bdcUser,
				manager: leadInputForm.prospect.manager,
				notes: leadInputForm.prospect.notes,
				category: leadInputForm.prospect.category,
				businessUnitName: leadInputForm.prospect.businessUnitName,
			},
			vehicle: {
				trade: {
					make: leadInputForm.vehicle.make,
					model: leadInputForm.vehicle.model,
					year: leadInputForm.vehicle.year,
					vin: leadInputForm.vehicle.vin,
					odometer: leadInputForm.vehicle.odometer,
				},
				desired: desiredVehicles,
			},
		};

		await createSalesLeadMutation({ input, callback });
	};

	const EmptyTitle = (props: Readonly<{ handleAddItem: () => void; hasPermission: boolean }>) => {
		const { handleAddItem = () => {}, hasPermission } = props;
		return (
			<Typography className="text-sm !font-montserrat !font-medium" component={"label"}>
				No Leads to show.{" "}
				<span
					className={clsx({
						"underline ": true,
						"text-blue-800 cursor-pointer": hasPermission,
						"text-disabled cursor-not-allowed": !hasPermission,
					})}
					onClick={handleAddItem}
				>
					Add the first one
				</span>
			</Typography>
		);
	};

	const snackBarsActions = () => {
		const key = useId();
		return <SnackbarsActions key={key} index={key} />;
	};

	const showSnackBar = (message: string, critical?: boolean) => {
		enqueueSnackbar(<SnackbarsContent message={message} critical={critical} />, {
			variant: critical ? "error" : "success",
			preventDuplicate: true,
			action: snackBarsActions,
		});
	};

	return (
		<Grid container alignContent="flex-start" className="h-full">
			<Grid container item>
				<Grid item xs={12} height={512}>
					{!dataToShow ? (
						<EmptyBody
							image={emptyTable}
							title={<EmptyTitle handleAddItem={handleOpenDialog} hasPermission={hasEditSalesLeadsAccess} />}
							bodyHeight={"435px"}
							className="!shadow-none"
						/>
					) : (
						<Stack className="h-full">
							<ContentTitle title={`${commissionCycle?.title} ${payPeriod?.title} Leads`} isLoading={isLoading} />
							<SalesLeadsTable
								rows={data}
								hasNextPage={hasNextPage}
								isFetching
								handleAddItem={handleOpenDialog}
								hasEditSalesLeadsAccess={hasEditSalesLeadsAccess}
							/>
						</Stack>
					)}

					<AddEditPopupDialog
						open={isDialogOpen}
						onCloseAction={handleCloseDialog}
						onAddAction={handleAddItem}
						canSave={!hasErrors}
					/>
				</Grid>
			</Grid>
		</Grid>
	);
}
